import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { Logo } from "@/components/Logo";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { login } = useAuth();
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      login(data.token, data.user);
      toast.success(t("auth.created"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 relative items-center justify-center overflow-hidden">
        <img src="https://images.unsplash.com/photo-1762279388952-85187155e48d" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 text-center px-10">
          <Logo size="lg" />
          <h2 className="mt-8 text-3xl font-bold text-white">{t("auth.joinTitle")}</h2>
          <p className="mt-3 text-zinc-400 max-w-sm mx-auto">{t("auth.joinSub")}</p>
        </div>
      </div>

      <div className="w-full lg:w-[460px] flex flex-col justify-center px-8 bg-[#060606]">
        <div className="flex justify-end mb-6"><LanguageToggle /></div>
        <div className="w-full max-w-sm mx-auto">
          <div className="lg:hidden mb-8 flex justify-center"><Logo size="lg" /></div>
          <h1 className="text-2xl font-bold text-white mb-1">{t("auth.register")}</h1>
          <p className="text-sm text-zinc-500 mb-8">{t("auth.registerSub")}</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("auth.fullName")}</label>
              <input data-testid="register-name-input" required value={name} onChange={(e) => setName(e.target.value)}
                className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("auth.email")}</label>
              <input data-testid="register-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} dir="ltr"
                className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white text-start focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] outline-none" placeholder="you@example.com" />
            </div>
            <div>
              <label className="text-xs font-semibold tracking-widest text-zinc-400 uppercase">{t("auth.password")}</label>
              <input data-testid="register-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} dir="ltr"
                className="mt-2 w-full bg-[#121212] border border-zinc-800 rounded-md px-4 py-3 text-white text-start focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] outline-none" />
            </div>
            <button data-testid="register-submit-button" type="submit" disabled={loading}
              className="w-full bg-[#00E5FF] text-black font-bold py-3 rounded-md hover:bg-[#33EBFF] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 size={18} className="animate-spin" />} {t("auth.createBtn")}
            </button>
          </form>
          <p className="mt-6 text-sm text-zinc-500 text-center">
            {t("auth.haveAccount")} <Link to="/login" data-testid="goto-login" className="text-[#00E5FF] hover:underline">{t("auth.login")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
